class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 350);

    const form = this.querySelector('form');

    if (form) {
      form.addEventListener('input', this.debouncedOnSubmit);
      form.addEventListener('change', this.debouncedOnSubmit);
    }
  }

  static setListeners() {
    window.addEventListener('popstate', (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;

      if (searchParams === FacetFiltersForm.searchParamsPrev) return;

      FacetFiltersForm.renderPage(searchParams, null, false);
    });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('facet-remove a').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event = null, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections = FacetFiltersForm.getSections();
    const gridContainer = document.getElementById('ProductGridContainer');
    const count = document.getElementById('ProductCount');
    const desktopCount = document.getElementById('ProductCountDesktop');

    gridContainer?.querySelector('.collection')?.classList.add('loading');
    count?.classList.add('loading');
    desktopCount?.classList.add('loading');

    sections.forEach((section) => {
      const url =
        `${window.location.pathname}?section_id=${section.section}` +
        `${searchParams ? '&' + searchParams : ''}`;

      const cached = FacetFiltersForm.filterData.find((item) => item.url === url);

      cached
        ? FacetFiltersForm.renderSectionFromCache(cached, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(searchParams);
    }
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Facet request failed: ${response.status}`);
        }

        return response.text();
      })
      .then((html) => {
        FacetFiltersForm.filterData.push({ html, url });
        FacetFiltersForm.renderAll(html, event);
      })
      .catch((error) => {
        console.error('999TEE facet AJAX error:', error);
      });
  }

  static renderSectionFromCache(cached, event) {
    FacetFiltersForm.renderAll(cached.html, event);
  }

  static renderAll(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    FacetFiltersForm.renderProductGridContainer(parsedHTML);
    FacetFiltersForm.renderFilters(parsedHTML, event);
    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderProductCount(parsedHTML);
    FacetFiltersForm.renderToolbar(parsedHTML);
    FacetFiltersForm.renderFilterButtonCount(parsedHTML);

    initNdFacets();
    initNdMobileFacetBarStop();
  }

  static renderProductGridContainer(parsedHTML) {
    const source = parsedHTML.getElementById('ProductGridContainer');
    const target = document.getElementById('ProductGridContainer');

    if (!source || !target) return;

    target.innerHTML = source.innerHTML;

    target.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });

    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger();
    }
  }

  static renderFilters(parsedHTML, event) {
    const source = parsedHTML.querySelector('#FacetsWrapperDesktop');
    const target = document.querySelector('#FacetsWrapperDesktop');

    if (!source || !target) return;

    const openFilters = Array.from(
      target.querySelectorAll('.nd-filter-group[open]')
    ).map((details) => details.id);

    const currentFilter = event?.target?.closest('.nd-filter-group');

    if (currentFilter && !openFilters.includes(currentFilter.id)) {
      openFilters.push(currentFilter.id);
    }

    target.innerHTML = source.innerHTML;

    openFilters.forEach((id) => {
      document.getElementById(id)?.setAttribute('open', '');
    });
  }

  static renderActiveFacets(parsedHTML) {
    const source = parsedHTML.querySelector('.active-facets-desktop');
    const current = document.querySelector('.active-facets-desktop');

    if (!source) {
      current?.remove();
      return;
    }

    if (current) {
      current.outerHTML = source.outerHTML;
      return;
    }

    document
      .querySelector('.nd-facets-toolbar')
      ?.insertAdjacentHTML('afterend', source.outerHTML);
  }

  static renderProductCount(parsedHTML) {
    const sourceCount = parsedHTML.getElementById('ProductCount');
    const sourceDesktop = parsedHTML.getElementById('ProductCountDesktop');

    const targetCount = document.getElementById('ProductCount');
    const targetDesktop = document.getElementById('ProductCountDesktop');

    if (sourceCount && targetCount) {
      targetCount.innerHTML = sourceCount.innerHTML;
      targetCount.classList.remove('loading');
    }

    if (sourceDesktop && targetDesktop) {
      targetDesktop.innerHTML = sourceDesktop.innerHTML;
      targetDesktop.classList.remove('loading');
    } else if (sourceCount && targetDesktop) {
      targetDesktop.innerHTML = sourceCount.innerHTML;
      targetDesktop.classList.remove('loading');
    }

    const drawerCount = document.querySelector('.nd-filter-show-count');

    if (drawerCount && sourceCount) {
      const number = sourceCount.textContent.match(/\d+/);

      if (number) {
        drawerCount.textContent = number[0];
      }
    }
  }

  static renderToolbar(parsedHTML) {
    const sourceSelect = parsedHTML.querySelector(
      '#FacetSortDrawerForm select[name="sort_by"]'
    );

    const targetSelect = document.querySelector(
      '#FacetSortDrawerForm select[name="sort_by"]'
    );

    if (sourceSelect && targetSelect) {
      targetSelect.value = sourceSelect.value;
    }

    const sourceMobile = parsedHTML.querySelector('#FacetSortMobileForm');
    const targetMobile = document.querySelector('#FacetSortMobileForm');

    if (sourceMobile && targetMobile) {
      targetMobile.innerHTML = sourceMobile.innerHTML;
    }
  }

  static renderFilterButtonCount(parsedHTML) {
    [
      '.nd-filter-open',
      '.nd-mobile-filter-open'
    ].forEach((selector) => {
      const source = parsedHTML.querySelector(selector);
      const target = document.querySelector(selector);

      if (source && target) {
        target.innerHTML = source.innerHTML;
      }
    });
  }

  static updateURLHash(searchParams) {
    const url = searchParams
      ? `${window.location.pathname}?${searchParams}`
      : window.location.pathname;

    history.pushState({ searchParams }, '', url);
  }

  static getSections() {
    const grid = document.getElementById('product-grid');

    return grid
      ? [{ section: grid.dataset.id }]
      : [];
  }

  createSearchParams(form) {
    return new URLSearchParams(new FormData(form)).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const filterForm = document.getElementById('FacetFiltersForm');
    const sortForm = document.getElementById('FacetSortDrawerForm');
    const queries = [];

    if (filterForm) {
      filterForm.querySelectorAll('.no-js-list').forEach((el) => el.remove());

      const params = this.createSearchParams(filterForm);

      if (params) {
        queries.push(params);
      }
    }

    if (sortForm) {
      const params = this.createSearchParams(sortForm);

      if (params) {
        queries.push(params);
      }
    }

    const merged = new URLSearchParams();

    queries.forEach((query) => {
      new URLSearchParams(query).forEach((value, key) => {
        key.startsWith('filter.')
          ? merged.append(key, value)
          : merged.set(key, value);
      });
    });

    this.onSubmitForm(merged.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();

    FacetFiltersForm.toggleActiveFacets();

    const href = event.currentTarget.href;

    const searchParams = href.includes('?')
      ? href.substring(href.indexOf('?') + 1)
      : '';

    FacetFiltersForm.renderPage(searchParams);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);

if (!customElements.get('facet-filters-form')) {
  customElements.define('facet-filters-form', FacetFiltersForm);
}

FacetFiltersForm.setListeners();


/* =========================================
   PRICE RANGE
========================================= */

class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.querySelectorAll('input').forEach((input) => {
      input.addEventListener('change', this.onRangeChange.bind(this));
    });

    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const [minInput, maxInput] = this.querySelectorAll('input');

    if (!minInput || !maxInput) return;

    if (maxInput.value) {
      minInput.setAttribute('max', maxInput.value);
    }

    if (minInput.value) {
      maxInput.setAttribute('min', minInput.value);
    }

    if (!minInput.value) {
      maxInput.setAttribute('min', 0);
    }

    if (!maxInput.value) {
      minInput.setAttribute('max', maxInput.getAttribute('max'));
    }
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const maxAttr = input.getAttribute('max');
    const max = maxAttr !== null ? Number(maxAttr) : Infinity;

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

if (!customElements.get('price-range')) {
  customElements.define('price-range', PriceRange);
}


/* =========================================
   FACET REMOVE
========================================= */

class FacetRemove extends HTMLElement {
  constructor() {
    super();

    const link = this.querySelector('a');

    if (!link) return;

    link.setAttribute('role', 'button');

    link.addEventListener('click', this.closeFilter.bind(this));

    link.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.closeFilter(event);
      }
    });
  }

  closeFilter(event) {
    event.preventDefault();

    const form =
      document.querySelector('.nd-filter-form') ||
      document.querySelector('facet-filters-form');

    if (form?.onActiveFilterClick) {
      form.onActiveFilterClick(event);
    }
  }
}

if (!customElements.get('facet-remove')) {
  customElements.define('facet-remove', FacetRemove);
}


/* =========================================
   ND FILTER DRAWER
========================================= */

function initNdFacets() {
  const drawer = document.querySelector('#NdFilterDrawer');
  const overlay = document.querySelector('.nd-filter-overlay');

  if (!drawer || !overlay) return;

  const openButtons = document.querySelectorAll(
    '.nd-filter-open, .nd-mobile-filter-open'
  );

  const closeButton = drawer.querySelector('.nd-filter-close');
  const showButton = drawer.querySelector('.nd-filter-show');

  const openDrawer = () => {
    drawer.classList.add('is-open');
    overlay.classList.add('is-active');
    drawer.setAttribute('aria-hidden', 'false');

    openButtons.forEach((button) => {
      button.setAttribute('aria-expanded', 'true');
    });

    document.body.classList.add('nd-filter-locked');
  };

  const closeDrawer = () => {
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-active');
    drawer.setAttribute('aria-hidden', 'true');

    openButtons.forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });

    document.body.classList.remove('nd-filter-locked');
  };

  openButtons.forEach((button) => {
    if (button.dataset.ndBound === 'true') return;

    button.dataset.ndBound = 'true';
    button.addEventListener('click', openDrawer);
  });

  if (closeButton && closeButton.dataset.ndBound !== 'true') {
    closeButton.dataset.ndBound = 'true';
    closeButton.addEventListener('click', closeDrawer);
  }

  if (overlay.dataset.ndBound !== 'true') {
    overlay.dataset.ndBound = 'true';

    overlay.addEventListener('click', () => {
      closeDrawer();
      closeNdMobileSort();
    });
  }

  if (showButton && showButton.dataset.ndBound !== 'true') {
    showButton.dataset.ndBound = 'true';
    showButton.addEventListener('click', closeDrawer);
  }

  initNdMobileSort();
}


/* =========================================
   MOBILE SORT
========================================= */

function initNdMobileSort() {
  const openButton = document.querySelector('.nd-mobile-sort-open');
  const sheet = document.querySelector('.nd-mobile-sort-sheet');
  const closeButton = document.querySelector('.nd-mobile-sort-close');
  const overlay = document.querySelector('.nd-filter-overlay');

  if (!openButton || !sheet || !overlay) return;

  if (openButton.dataset.ndBound !== 'true') {
    openButton.dataset.ndBound = 'true';

    openButton.addEventListener('click', () => {
      sheet.classList.add('is-open');
      overlay.classList.add('is-active');
      openButton.setAttribute('aria-expanded', 'true');
      document.body.classList.add('nd-filter-locked');
    });
  }

  if (closeButton && closeButton.dataset.ndBound !== 'true') {
    closeButton.dataset.ndBound = 'true';
    closeButton.addEventListener('click', closeNdMobileSort);
  }

  sheet.querySelectorAll('input[name="sort_by"]').forEach((input) => {
    if (input.dataset.ndBound === 'true') return;

    input.dataset.ndBound = 'true';

    input.addEventListener('change', () => {
      const desktopSort = document.querySelector(
        '#FacetSortDrawerForm select[name="sort_by"]'
      );

      if (desktopSort) {
        desktopSort.value = input.value;

        desktopSort.dispatchEvent(
          new Event('input', { bubbles: true })
        );
      }

      closeNdMobileSort();
    });
  });
}

function closeNdMobileSort() {
  const sheet = document.querySelector('.nd-mobile-sort-sheet');
  const overlay = document.querySelector('.nd-filter-overlay');
  const openButton = document.querySelector('.nd-mobile-sort-open');
  const filterDrawer = document.querySelector('#NdFilterDrawer');

  sheet?.classList.remove('is-open');
  openButton?.setAttribute('aria-expanded', 'false');

  if (!filterDrawer?.classList.contains('is-open')) {
    overlay?.classList.remove('is-active');
    document.body.classList.remove('nd-filter-locked');
  }
}


/* =========================================
   MOBILE SORT / FILTER BAR FOOTER STOP
========================================= */

let ndFacetFooterObserver = null;

function initNdMobileFacetBarStop() {
  const bar = document.querySelector('.nd-mobile-facets-bar');

const footer =

  document.querySelector('[id*="__footer_"]') ||

  document.querySelector('footer');


  if (!bar || !footer) return;

  if (ndFacetFooterObserver) {
    ndFacetFooterObserver.disconnect();
  }

  const hideBar = () => {
    bar.classList.add('nd-mobile-facets-bar--hidden');
  };

  const showBar = () => {
    bar.classList.remove('nd-mobile-facets-bar--hidden');
  };

  ndFacetFooterObserver = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];

      if (window.innerWidth > 749) {
        showBar();
        return;
      }

      if (entry.isIntersecting) {
        hideBar();
      } else {
        showBar();
      }
    },
    {
      root: null,
      threshold: 0,
      rootMargin: '0px 0px 58px 0px'
    }
  );

  ndFacetFooterObserver.observe(footer);

  const footerRect = footer.getBoundingClientRect();

  if (
    window.innerWidth <= 749 &&
    footerRect.top <= window.innerHeight + 58 &&
    footerRect.bottom >= 0
  ) {
    hideBar();
  } else {
    showBar();
  }
}


/* =========================================
   ESC KEY
========================================= */

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;

  const drawer = document.querySelector('#NdFilterDrawer');
  const overlay = document.querySelector('.nd-filter-overlay');

  if (drawer?.classList.contains('is-open')) {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');

    document
      .querySelectorAll('.nd-filter-open, .nd-mobile-filter-open')
      .forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
      });
  }

  overlay?.classList.remove('is-active');

  closeNdMobileSort();

  document.body.classList.remove('nd-filter-locked');
});


/* =========================================
   INITIALISE
========================================= */

document.addEventListener('DOMContentLoaded', () => {
  initNdFacets();
  initNdMobileFacetBarStop();
});

document.addEventListener('shopify:section:load', () => {
  initNdFacets();
  initNdMobileFacetBarStop();
});